import { useCallback, useEffect, useMemo, useRef } from 'react'
import { type UseFormReturn, useWatch } from 'react-hook-form'
import type { WorkwiseFormValues } from '@/types/workwise'
import {
  AUTO_FIELD_PATHS,
  type AutoFieldPath,
  type AutoSuggestion,
  shouldApplySuggestion,
  suggestFields,
} from '@/utils/workwise/autoFields'

export interface AutoFieldExtras {
  appNext?: string | null
  dbNext?: string | null
  gatewayNext?: string | null
  adminOsType?: string | null
  defaultVersion?: string
  owner?: string
}

/**
 * Keeps the auto-filled WorkWise fields (slug, VM names, OS, tenant name,
 * DB connection reference, version, owner) in step with their inputs, without
 * ever overwriting a value the user has typed. See utils/workwise/autoFields.ts
 * for the rule.
 */
export function useWorkwiseAutoFields(form: UseFormReturn<WorkwiseFormValues>, extras: AutoFieldExtras) {
  const [customerName, environment, customerNumber] = useWatch({
    control: form.control,
    name: ['customer.name', 'environment', 'customer.number'],
  })
  const { appNext, dbNext, gatewayNext, adminOsType, defaultVersion, owner } = extras

  const suggestions = useMemo(
    () =>
      suggestFields({
        customerName,
        environment,
        customerNumber,
        appNext,
        dbNext,
        gatewayNext,
        adminOsType,
        defaultVersion,
        owner,
      }),
    [customerName, environment, customerNumber, appNext, dbNext, gatewayNext, adminOsType, defaultVersion, owner],
  )

  const applied = useRef<Partial<Record<AutoFieldPath, AutoSuggestion>>>({})

  useEffect(() => {
    for (const path of AUTO_FIELD_PATHS) {
      const next = suggestions[path]
      const current = String(form.getValues(path) ?? '')
      if (shouldApplySuggestion(current, next, applied.current[path])) {
        form.setValue(path, next.value as never, { shouldValidate: Boolean(form.getFieldState(path).error) })
      }
      applied.current[path] = next
    }
  }, [form, suggestions])

  /** Puts the suggested value back (the "Use suggested" buttons). */
  const restoreSuggestion = useCallback(
    (path: AutoFieldPath) => {
      form.setValue(path, suggestions[path].value as never, { shouldValidate: true, shouldDirty: true })
    },
    [form, suggestions],
  )

  return { suggestions, applySuggestion: restoreSuggestion }
}
